import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import { useT } from "../../../../core/i18n";
import Api from "../../../../core/data/redux/utils/apiCall";
import {
  downloadAchcLetter,
  facilityLabel,
  findOneAchc,
  formatSapDate,
  formatSapDateTime,
  getLoggedInEmpId,
  getMaritalLabel,
  getRemarksList,
  getStatusBadgeClass,
  getStatusLabel,
  openAchcDocumentInNewTab,
} from "./authorityLetterHealthCheckup.service";

const ViewAuthorityLetter: React.FC = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const routes = all_routes;
  const { referenceNumber } = useParams<{ referenceNumber: string }>();

  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const refId = referenceNumber ? decodeURIComponent(referenceNumber) : "";

  useEffect(() => {
    const load = async () => {
      if (!refId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await findOneAchc(refId);
        setDetail(data);
      } catch (err: any) {
        setDetail(null);
        toast.error(Api.getErrorMessage(err) || t("failed_to_load_details"));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [refId, t]);

  const status = String(detail?.RequestStatus ?? "").trim().toUpperCase();
  const canDownload = status === "D";
  const remarks = getRemarksList(detail);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    // Open the tab in the same click so the browser does not block it after the API returns.
    const previewWindow = window.open("about:blank", "_blank");
    try {
      const empId = String(detail?.EmpID || getLoggedInEmpId() || "").trim();
      const doc = await downloadAchcLetter(empId, detail?.ReferenceNumber || refId);
      openAchcDocumentInNewTab(doc, previewWindow);
    } catch (err: any) {
      if (previewWindow && !previewWindow.closed) previewWindow.close();
      toast.error(err?.message || Api.getErrorMessage(err) || "Download failed. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">
              {t("authority_letter_for_annual_comprehensive_health_checkup")}
            </h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.authorityLetterHealthCheckup.list}>
                    {t("authority_letter_for_annual_comprehensive_health_checkup")}
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {t("view_details")}
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex gap-2">
            {canDownload && detail && (
              <button className="btn btn-danger" onClick={handleDownload} disabled={downloading}>
                {downloading ? (
                  <span className="spinner-border spinner-border-sm me-1" />
                ) : (
                  <i className="ti ti-download me-1"></i>
                )}
                {t("download_approval_letter") || "Download Authority Letter"}
              </button>
            )}
            <button className="btn btn-light" onClick={() => navigate(-1)}>
              <i className="ti ti-arrow-left me-1"></i>
              {t("close")}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-danger" role="status" />
          </div>
        ) : !detail ? (
          <div className="alert alert-light border">{t("no_details_available")}</div>
        ) : (
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between gap-3 mb-2">
                <span className="text-muted fw-semibold">{t("request_status")}:</span>
                <span className={getStatusBadgeClass(detail.RequestStatus)}>
                  {getStatusLabel(detail.RequestStatus, t)}
                </span>
              </div>
              <Row label={t("reference_number") || t("reference_no")} value={detail.ReferenceNumber} />
              <Row label={t("employee_id")} value={detail.EmpID} />
              <Row label={t("hospital_name")} value={detail.HospitalName} />
              <Row
                label={t("facility_required_for")}
                value={facilityLabel(detail.ComprehensiveCheckupFor)}
              />
              <Row label={t("marital_status")} value={getMaritalLabel(detail.MaritalStatus)} />
              <Row
                label={t("request_creation_date")}
                value={
                  detail.RequestCreationDate ||
                  (detail.created_at ? formatSapDate(detail.created_at) : "-")
                }
              />
              {remarks.length > 0 && (
                <div className="mt-3 pt-3 border-top">
                  <div className="fw-semibold mb-2">{t("remarks")}</div>
                  {remarks.map((r: any, idx: number) => (
                    <div key={idx} className="border rounded p-2 mb-2">
                      <div>{r.Remarks ?? r.remarks ?? r.HRemarks ?? "-"}</div>
                      <div className="small text-muted mt-1">
                        {r.ApproverName ?? r.approverName ?? ""}
                        {(r.TimeStamp || r.createdAt) &&
                          `  •  ${formatSapDateTime(r.TimeStamp || r.createdAt)}`}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Row: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div className="d-flex justify-content-between gap-3 mb-2">
    <span className="text-muted fw-semibold">{label}:</span>
    <span className="text-dark text-end">{value ?? "-"}</span>
  </div>
);

export default ViewAuthorityLetter;
